/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import _ from 'lodash';
import { subsidyUsed } from 'hooks/round3/subsidy';
import cn from 'classnames';
import { Link } from 'react-router-dom';

// Hooks
import { useSelector } from 'react-redux';
import useAdmission from 'hooks/round3/useAdmission';
import { hasValidPriorities, PRIORITY_ERROR } from 'hooks/round3/priorities';
import useIsMobile from 'hooks/useIsMobile';
import { notApplicable, getProjectScore, getFullScore } from 'helpers';
import { PREV_TCAS_LABEL } from 'constants/school-year';
import PassChance from './PassChance';
import { LATE_FEE, LATE_PAY_FIRST, LATE_R3_ADD_ONLY, isLatePendingAdmission, lateRankingAllowed } from 'late-fee';

const prices = [150, 200, 250, 300, 400, 500, 600, 700, 800, 900];
const AdmissionConfirm = ({ favorites, studentStatus, alreadySetZero, redeemableAmount, redeemedAmount, quotaUsed, admissions = [], onEdit, onBack, phase }) => {
  const profile = useSelector((state) => _.get(state, 'app.user') || {});
  const courseMap = useSelector((state) => _.get(state, 'app.courseMap')) || {};
  const admissionCountMap = useSelector((state) => _.get(state, 'app.admissionCountMap')) || {};
  const { projects } = useAdmission(favorites, studentStatus);
  const canConfirm = hasValidPriorities(projects) && projects.every(({ program_project, priority }) => (
    courseMap[program_project] && !notApplicable(profile, courseMap[program_project], { studentStatus, favorites: projects, nextPriority: priority }).length
  ));
  const skipRedemption = _.get(profile, 'skip_r3_redemption');

  const paidAdmissions = admissions.filter(({ status }) => status === 'paid');
  const paidAmount = _.sumBy(paidAdmissions, ({ payment_amount }) => payment_amount) + subsidyUsed({ alreadySetZero, skipRedemption, redeemableAmount, redeemedAmount });
  const paidSubjectCount = prices.indexOf(paidAmount) + 1;
  // const paidSubjectCount = (_.get(_.maxBy(paidAdmissions, 'total_amount'), 'program_projects') || []).length;
  let amount = projects.length > paidSubjectCount
    ? (prices[projects.length - 1] - paidAmount)
    : 0;
  if (amount <= redeemableAmount && !skipRedemption) {
    amount = 0;
  }
  // สมัครล่าช้า (ทปอ. 17 ต.ค. 69): ทุกการยืนยัน +200 แม้ไม่มีค่าสาขาเพิ่ม (สลับอันดับก็ 200) — src/late-fee.js
  const lateFee = phase === 'late' ? LATE_FEE : 0;
  const subjectOwed = Math.max(0, amount - redeemableAmount);
  // ใบล่าช้าค้างจ่าย = ยืนยันเพิ่มไม่ได้ (BE 400 LATE_PAY_FIRST)
  const latePending = isLatePendingAdmission(_.last(admissions));
  // ช่วงล่าช้า: สลับ/ลด/แทนอันดับที่มีผลแล้วอย่างเดียวไม่ได้ ต้องเพิ่มสาขา (BE 400 LATE_R3_ADD_ONLY — กันก่อนกรอก OTP)
  const lateRankBlocked = phase === 'late' && !lateRankingAllowed(admissions, projects);

  const [errorActive, setErrorActive] = useState(false);
  const isMobile = useIsMobile();

  return (
    <>
      <p>{'ท่านเลือกไว้ทั้งหมด'} {projects.length} {'อันดับ'} <b className="color-blue">{'รอยืนยันการสมัคร'}</b></p>
      <ul className="r-choose -confirmed">
        {(projects || []).map((row, index) => {
          const lang = 'th';
          const project = courseMap[row.program_project];
          const majorName = _.get(project, `major_name_${lang}`);
          const projectName = _.get(project, `project_name_${lang}`);
          const errors = notApplicable(profile, project, { studentStatus, favorites, nextPriority: row.priority });
          if (!project) return null;
          const admissionCountKey = `${project.program_id || 0}_${project.major_id || 0}_${project.project_id || 0}`;
          return (
            <li key={index} className={cn({ error: Boolean(errors.length) })}>
              <label style={{ pointerEvents: 'initial', ...(isMobile ? { paddingBottom: 60 } : {}) }}>
                <div className="order">{'อันดับที่'} {row.priority}</div>
                <h4 className="score" style={isMobile ? { bottom: 38 } : {}}>
                  {'คะแนน'} {getProjectScore(profile, project, row.priority)}/{getFullScore(project)}
                  {!errors.length && (
                    <PassChance score={getProjectScore(profile, project, row.priority)} project={project} />
                  )}
                </h4>
                <h4
                  className="score"
                  style={isMobile
                    ? { right: 'auto', left: 'auto', bottom: 16, textAlign: 'left', pointerEvents: 'none' }
                    : { right: 16, left: 'auto', pointerEvents: 'none' }}
                >
                  {`จำนวนผู้สมัคร ${(admissionCountMap[admissionCountKey] || 0).toLocaleString()} คน`}
                  {Number(_.get(project, 'last_year_register')) ? (
                    <span style={{ color: '#999' }}>{` (${PREV_TCAS_LABEL} : ${Number(_.get(project, 'last_year_register')).toLocaleString()} คน)`}</span>
                  ) : null}
                </h4>
                <i className="i-drag" />
                <span
                  className="u-logo"
                  style={{ backgroundImage: `url('/assets/img/logo/${_.get(project, 'university_id')}.png')` }}
                />
                <strong>{_.get(project, `university_name_${lang}`)}</strong>
                {_.get(project, `campus_name_${lang}`)}
                <br />
                {_.get(project, `faculty_name_${lang}`)}
                <br />
                {_.get(project, `field_name_${lang}`)}
                <br />
                {majorName && majorName !== '0' ? (
                  <>
                    {'วิชาเอก'}
                    {majorName}
                    <br />
                  </>
                ) : null}
                {_.get(project, `program_name_${lang}`)}
                <br />
                {lang === 'th' ? `(${_.get(project, `program_type_name_th`)})` : null}
                {projectName && projectName !== '0' ? (
                  <>
                    <br />
                    {'โครงการ '}
                    {projectName}
                  </>
                ) : null}
              </label>
              {Boolean(errors.length) && (
                <div className={cn('t-collapse', { active: errorActive })}>
                  <div className="t-toggle" onClick={() => setErrorActive(_active => !_active)}>
                    {'ไม่ผ่านเงื่อนไขการรับ ไม่สามารถสมัครได้'}<i className="i-toggle i-down-red"></i>
                  </div>
                  <div className="t-content">
                    <ul>
                      {errors.map(({ messages = [], key, label, expected, value }) => {
                        if (messages.length > 1) {
                          return (
                            <li key={key}>
                              <i className="i-no"></i>
                              <span>{messages[0].label}</span>
                              {messages.map(({ expected, value }, index) => (
                                <div key={index}>
                                  <b>{expected}</b>
                                  <span>({'ผู้สมัคร'}: <b className="color-red">{value}</b>)</span>
                                </div>
                              ))}
                            </li>
                          )
                        }
                        if (!value && value !== 0) {
                          return (
                            <li key={key}>
                              <i className="i-no"></i>
                              <span>{label}</span>
                            </li>
                          )
                        }
                        return (
                          <li key={key}>
                            <i className="i-no"></i>
                            <span>{label}</span>
                            <b>{expected}</b>
                            <span>({'ผู้สมัคร'}: <b className="color-red">{value}</b>)</span>
                          </li>
                        )
                      })}
                    </ul>
                  </div >
                </div >
              )}
            </li>
          )
        })}
        {Boolean(redeemableAmount) && (
          <li className="noti _heading">
            <strong className="color-orange" style={{ fontSize: '1.2em' }}>
              <span>สมัครครั้งแรก</span>
              <span style={{ textDecoration: 'underline' }}>{lateFee ? 'ไม่มีค่าสมัคร' : 'ไม่มีค่าใช้จ่าย'}</span>
            </strong>
            {Boolean(lateFee) && <div className="color-red">{`ช่วงสมัครล่าช้า ชำระเฉพาะค่าธรรมเนียมสมัครล่าช้า ${LATE_FEE} บาท`}</div>}
            <div>ผู้สมัครขอรับเงินสนับสนุนค่าสมัครคัดเลือก รอบที่ 3 Admissions ได้ตามจำนวนอันดับที่ยื่นสมัคร</div>
            <div className="s-flex -wrap -gap-x color-orange">
              {phase !== 'late' && (
                <div className="s-flex gap-1">
                  <img src="/assets/img/i/i-check-orange.svg" alt="" width={16} height={16} />
                  <strong>ฟรี</strong>
                </div>
              )}
              <div className="s-flex gap-1">
                <img src="/assets/img/i/i-check-orange.svg" alt="" width={16} height={16} />
                <strong>ตัดสินใจได้ 3 ครั้ง</strong>
              </div>
              <div className="s-flex gap-1">
                <img src="/assets/img/i/i-check-orange.svg" alt="" width={16} height={16} />
                <strong>สูงสุด 7 อันดับ</strong>
              </div>
            </div>
          </li>
        )}
      </ul>
      <div className="t-summary">
        <dl>
          <dt>{'จำนวนอันดับสาขาวิชา (เลือกได้สูงสุด 10 อันดับ)'}</dt>
          <dd><b>{(projects || []).length}</b> {'อันดับ'}</dd>
        </dl>
        {!paidAmount ? (
          <>
            {Boolean(redeemableAmount) && (
              <dl>
                <dt>
                  <span>{'ยอดเงินสนับสนุนทั้งหมด'}</span>
                </dt>
                <dd><b>{redeemableAmount.toFixed(2)}</b> {'บาท'}</dd>
              </dl>
            )}
            {amount > redeemableAmount && (
              <dl>
                <dt>
                  <span className="color-red">{'ยอดค้างชำระทั้งหมด'}</span>
                </dt>
                <dd className="color-red"><b>{(amount - redeemableAmount).toFixed(2)}</b> {'บาท'}</dd>
              </dl>
            )}
          </>
        ) : (amount > 0 && amount > redeemableAmount) ? (
          <>
            <dl>
              <dt>
                <span>
                  {'ยอดชำระเงิน'} ({'ชำระมาแล้ว'} {paidSubjectCount} {'อันดับ'})
                </span>
              </dt>
              <dd><b>{(projects || []).length - paidSubjectCount}</b> {'อันดับ'}</dd>
            </dl>
            <dl>
              <dt>
                <span className="color-red">
                  {'ยอดค้างชำระทั้งหมด'} ({'จ่ายเพิ่ม'} {(projects || []).length - paidSubjectCount} {'อันดับ'})
                </span>
              </dt>
              <dd className="color-red"><b>{(amount - redeemableAmount).toFixed(2)}</b> {'บาท'}</dd>
            </dl>
          </>
        ) : !amount ? (
          <>
            {paidSubjectCount > projects.length && (
              <dl>
                <dt>
                  <span className="color-teal">{'สามารถเลือกได้อีก'} <b>{paidSubjectCount - projects.length}</b> {'อันดับโดยไม่ต้องชำระเงินเพิ่ม'}</span>
                </dt>
              </dl>
            )}
            <dl>
              <dt>
                <span className="color-green">{'ยอดค้างชำระทั้งหมด'}</span>
              </dt>
              <dd className="color-green"><b>0.00</b> {'บาท'}</dd>
            </dl>
          </>
        ) : null}
        {Boolean(lateFee) && (
          <>
            <dl data-testid="r3-summary-late-fee">
              <dt>
                <span className="color-red">{'ค่าธรรมเนียมสมัครล่าช้า'}</span>
              </dt>
              <dd className="color-red"><b>{lateFee.toFixed(2)}</b> {'บาท'}</dd>
            </dl>
            <dl data-testid="r3-summary-late-total">
              <dt>
                <span className="color-red">{'รวมยอดที่ต้องชำระ'}</span>
              </dt>
              <dd className="color-red"><b>{(subjectOwed + lateFee).toFixed(2)}</b> {'บาท'}</dd>
            </dl>
          </>
        )}
      </div>
      <p>
        <a
          className="btn-main cursor-pointer"
          onClick={() => window.open('/profile/rank-check-round3?from=confirm', '_blank')}
          style={{ backgroundImage: 'linear-gradient(to right, #1EBDE5, #2E6CB2)', backgroundSize: '100% 100%', minWidth: 268, justifyContent: 'center' }}
        >
          <img src="/assets/img/i/i-rank.svg" width="18" height="18" alt="" style={{ marginRight: 8, verticalAlign: 'middle' }} />
          {'เช็คเกณฑ์การจัดอันดับ'}
        </a>
      </p>
      <a className="btn-ghost cursor-pointer" onClick={onEdit}>
        {'แก้ไขสาขาวิชาที่สมัคร'}
      </a>
      <p className="color-gray">
        {'การสมัครนี้จะสมบูรณ์ เมื่อท่านกดยืนยันการสมัคร '}
        {(!redeemableAmount || lateFee) ? 'และได้ชำระเงินครบถ้วนแล้ว' : ''}
      </p>
      <div className="i-hearts">
        <i className={cn('i-heart', { '-blink': quotaUsed === 2, '-used': quotaUsed > 2 })} />
        <i className={cn('i-heart', { '-blink': quotaUsed === 1, '-used': quotaUsed > 1 })} />
        <i className={cn('i-heart', { '-blink': quotaUsed === 0, '-used': quotaUsed > 0 })} />
        {!quotaUsed
          ? <small>{'สามารถตัดสินใจได้ 3 ครั้ง'}</small>
          : <small>{'สามารถยืนยันการสมัครได้อีก'} {3 - quotaUsed} {'ครั้ง'}</small>}
      </div>
      {latePending ? <p role="alert" className="color-red">{LATE_PAY_FIRST}</p>
        : lateRankBlocked ? <p role="alert" className="color-red">{LATE_R3_ADD_ONLY}</p> : canConfirm ? <p>
        <Link to="/profile/apply-round3" className="btn-icon">
          <span className="icon"> <i className="i-heart"></i><small>-1</small></span>
          {/* แทนครั้งที่ 3 (ใบถูกยกเลิก/หมดอายุ) = ยังเป็นครั้งที่ 3/3 */}
          <b className="text">{'ยืนยันการสมัคร'} {'ครั้งที่'} {Math.min(quotaUsed + 1, 3)}/3</b>
        </Link>
      </p> : <p role="alert" className="color-red">{PRIORITY_ERROR}</p>}
      {Boolean(quotaUsed) && (
        <a className="btn-underline cursor-pointer" onClick={onBack}>
          {'ยกเลิกและกลับไปใช้การยืนยันครั้งที่'} {quotaUsed}/3
        </a>
      )}
    </>
  )
}

export default AdmissionConfirm;
